import { isEmpty } from "ramda";
import React, { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Alert, Autocomplete, Button, Loading, Modal, Spinner, TextInput, Typography } from "@/components/atoms";
import { useGetCountryStatesQuery } from "@/redux/apis/resource/resourceApi";
import { useStartVeivoValidationFromVinMutation, VeivoValidationFromVin } from "@/redux/apis/vehicle/veivoApi";
import { useGetVehicleFormOptionsQuery } from "@/redux/apis/vehicle/vehicleApi";
import { useOperator } from "@/hooks";
import { addToast, formatCurrency } from "@/utils";
import { getErrorMessages } from "@/helpers/reduxHelpers";

type StateOption = { name: string; value: string };

interface AddVehicleVinModalProps {
  open: boolean;
  onClose: () => void;
  /** Hands the started validation to the KYV flow (documents + walk-around). */
  onStarted: (validation: VeivoValidationFromVin) => void;
}

/** Same rule as the plate flow's input (AddVehicleModal). */
const REGO_PATTERN = "[A-Za-z0-9\\-]+";
const REGO_MAX_LENGTH = 32;

/**
 * Whitespace goes on the way IN, not just at submit: a plate copied off a
 * document often brings a space or a line break, and the pattern would then
 * refuse it with no obvious reason. Hyphens stay — the server ignores them
 * when matching, but they are how some plates are written.
 */
export const sanitiseRego = (raw: string): string => raw.replace(/\s+/g, "").toUpperCase();

/**
 * KYV entry point: plate number + state (DEV-1786).
 *
 * The operator no longer types the VIN. Veivo reads it off the registration
 * document and the VIN photo after the walk-around must match it, so a VIN
 * typo cannot start a verification for the wrong car. The plate here is NOT a
 * registry lookup (only Australia has a registry to ask): it names the car
 * while the VIN is unknown, is checked against every document, and lets the
 * server refuse a car already in the fleet before anything is spent.
 *
 * Duplicates are therefore caught twice: by state + plate here, and by VIN once
 * the registration document is read — that one arrives mid-flow as the
 * validation's `refusalCode` (see KyvVerificationFlow).
 *
 * State is collected because `vehicle.state_uuid` is required and travels to
 * Veivo as a claim to check against the registration document rather than
 * simply trusted.
 */
export const AddVehicleVinModal = ({ open, onClose, onStarted }: AddVehicleVinModalProps) => {
  const { id: operatorId, country, currency } = useOperator();
  // DEV-1732: the fee is charged when the verification completes — the moment
  // the paid lookups are spent — so it is agreed to here, before it starts.
  const { data: formOptions } = useGetVehicleFormOptionsQuery();
  const fee = formOptions?.fees.verificationFee ?? 0;
  const feeLabel = fee > 0 ? formatCurrency(fee, 2, currency) : null;
  const [rego, setRego] = useState("");
  const [selectedState, setSelectedState] = useState<StateOption | null>(null);
  const [query, setQuery] = useState("");

  const { isFetching, data: response } = useGetCountryStatesQuery(country);
  const [startFromVin, { isLoading, error, reset }] = useStartVeivoValidationFromVinMutation();
  // DEV-1746 / DEV-1786: the server refuses a car that is already one of this
  // operator's vehicles (same state and plate) before any verification starts,
  // and says which vehicle it is.
  const refusal = (error as { data?: { code?: string; vehicleUuid?: string } } | undefined)?.data;
  const existingVehicleUuid = refusal?.code === "VEHICLE_ALREADY_IN_FLEET" ? refusal.vehicleUuid : undefined;

  const options = useMemo(
    () => response?.data.states.map((state) => ({ name: state.name, value: state.id })) ?? [],
    [response?.data.states]
  );

  const filteredOptions = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter((o) => o.name.toLowerCase().includes(q));
  }, [options, query]);

  const normalisedRego = sanitiseRego(rego);
  const canSubmit = normalisedRego.length > 0 && Boolean(selectedState);

  const handleClose = () => {
    setRego("");
    setSelectedState(null);
    setQuery("");
    reset();
    onClose();
  };

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!normalisedRego || !selectedState) return;

    startFromVin({ registration_number: normalisedRego, state_uuid: selectedState.value })
      .unwrap()
      .then(onStarted)
      // Surface the server's own message (a car registered by another
      // operator, a missing state) rather than a generic failure.
      .catch((e) => {
        // Shown inline with a link to the vehicle instead of as a toast.
        if (e?.data?.code === "VEHICLE_ALREADY_IN_FLEET") return;
        getErrorMessages(e).forEach((m) => addToast("danger", m));
      });
  };

  return (
    <Modal open={open} onClose={handleClose} className="w-[85vw] !p-6 md:w-[400px] lg:w-[400px]">
      <div className="flex min-h-[500px] flex-col">
        <Typography variant="title">Add Vehicle</Typography>
        <Typography variant="paragraph" className="mt-1 text-neutral-dark-gray">
          Enter the vehicle&apos;s plate number and state to begin verification
        </Typography>

        <div className="relative mt-5 flex flex-1 flex-col">
          {isLoading && <Loading />}

          <form className="flex flex-1 flex-col" onSubmit={handleSubmit}>
            <label htmlFor="modal-rego" className="flex flex-col gap-1">
              <Typography variant="paragraph">Vehicle Plate Number</Typography>
              <TextInput
                title="Only letters, numbers and hyphens are allowed"
                pattern={REGO_PATTERN}
                placeholder="Enter Vehicle Plate Number"
                id="modal-rego"
                value={rego}
                onChange={(e) => {
                  if (error) reset();
                  setRego(sanitiseRego(e.target.value));
                }}
                hasError={Boolean(error)}
                required
                autoFocus
                maxLength={REGO_MAX_LENGTH}
                className="uppercase"
              />
              <Typography variant="small" className="text-neutral-dark-gray">
                The VIN is read from the registration document in the next step.
              </Typography>
            </label>

            <label htmlFor="modal-state" className="mt-5 flex flex-col gap-1">
              <Typography variant="paragraph">Select the State of Your Vehicle</Typography>
              <Autocomplete<StateOption>
                className="w-full"
                placeholder="Search State"
                options={filteredOptions}
                value={selectedState}
                onValueChange={setSelectedState}
                onQueryChange={setQuery}
                isLoading={isFetching}
                disabled={isEmpty(options) || isFetching}
              />
            </label>

            {existingVehicleUuid && (
              <Alert
                type="warning"
                className="mt-5"
                message={
                  <span>
                    This vehicle is already in your fleet.{" "}
                    <Link to={`/operator/${operatorId}/vehicles/my-vehicles/${existingVehicleUuid}`} className="font-semibold underline" onClick={handleClose}>
                      Open it
                    </Link>
                  </span>
                }
              />
            )}

            <div className="mt-auto flex flex-col gap-3">
              {feeLabel && (
                <Typography variant="small" className="text-center text-neutral-dark-gray">
                  Verifying a vehicle costs {feeLabel}, charged when the verification completes. Nothing is charged if you stop
                  before then.
                </Typography>
              )}
              <Button
                type="submit"
                variant="primary"
                size="lg"
                className="w-full"
                disabled={isLoading || !canSubmit}
              >
                {isLoading && <Spinner />}
                {feeLabel ? `Verify Vehicle for ${feeLabel}` : "Verify Vehicle"}
              </Button>
              <Button type="button" variant="secondary" size="lg" className="w-full" onClick={handleClose}>
                Cancel
              </Button>
            </div>
          </form>
        </div>
      </div>
    </Modal>
  );
};
